import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Link,
  Alert,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const QRCodeManagement = () => {
  useParams();
  const { user } = useAuth();
  const [area, setArea] = useState("メインフロア");
  const [startNumber, setStartNumber] = useState("1");
  const [endNumber, setEndNumber] = useState("3");
  const [generatedQRCodes, setGeneratedQRCodes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const areas = ["テラス", "喫煙席", "個室", "カウンター", "メインフロア"];

  const handleGenerate = async () => {
    try {
      if (!user?.storeId) {
        setError("店舗情報が見つかりません");
        return;
      }

      setLoading(true);
      setError(null);

      const start = parseInt(startNumber);
      const end = parseInt(endNumber);
      const newCodes = [];

      for (let i = start; i <= end; i++) {
        const response = await api.post("/api/qr/generate", {
          storeId: user.storeId,
          tableNumber: i.toString(),
          area: area,
        });

        if (response.data && response.data.qrData) {
          const qrData = response.data.qrData;
          // sessionIdはURL不要（テーブルアクセス時に自動判定/生成される）
          newCodes.push({
            ...qrData,
            tableNumber: i.toString(),
            orderUrl: `${window.location.origin}/store/${
              user.storeId
            }/menu?table=${i}&area=${encodeURIComponent(area)}`,
          });
        }
      }

      setGeneratedQRCodes([...generatedQRCodes, ...newCodes]);
    } catch (error) {
      console.error("QRコード生成エラー:", error);
      if (error.response?.status === 401) {
        setError("認証の有効期限が切れました。再度ログインしてください。");
      } else {
        setError(
          error.response?.data?.message || "QRコードの生成に失敗しました"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setError("");
  };

  const handleDownloadPDF = async () => {
    const doc = new jsPDF();

    for (let i = 0; i < generatedQRCodes.length; i++) {
      const element = document.getElementById(`qr-code-card-${i}`);
      if (!element) continue;

      const canvas = await html2canvas(element);
      const imgData = canvas.toDataURL("image/png");

      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      if (i !== 0) {
        doc.addPage();
      }

      doc.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    }

    doc.save("qrcodes.pdf");
  };

  return (
    <Box aria-busy={loading}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h5" gutterBottom>
        QRコード管理
      </Typography>

      {error && (
        <Alert severity="error" onClose={handleCloseError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>エリア</InputLabel>
              <Select
                value={area}
                label="エリア"
                onChange={(e) => setArea(e.target.value)}
                disabled={loading}
              >
                {areas.map((areaOption) => (
                  <MenuItem key={areaOption} value={areaOption}>
                    {areaOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="開始番号"
              type="number"
              value={startNumber}
              onChange={(e) => setStartNumber(e.target.value)}
              inputProps={{ min: 1 }}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="終了番号"
              type="number"
              value={endNumber}
              onChange={(e) => setEndNumber(e.target.value)}
              inputProps={{ min: 1 }}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading || !area || !startNumber || !endNumber}
              fullWidth
              sx={{ height: "100%" }}
            >
              {loading ? <CircularProgress size={24} /> : "QRコード生成"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {generatedQRCodes.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleDownloadPDF}
              disabled={loading || generatedQRCodes.length === 0}
            >
              PDFでダウンロード
            </Button>
          </Box>
          <Grid container spacing={2}>
            {generatedQRCodes.map((qrCode, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card id={`qr-code-card-${index}`}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {qrCode.area} - テーブル{qrCode.tableNumber}
                    </Typography>
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                    >
                      <QRCodeSVG
                        value={qrCode.orderUrl}
                        size={200}
                        level="H"
                        includeMargin={true}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      注文URL:
                    </Typography>
                    <Link
                      href={qrCode.orderUrl}
                      target="_blank"
                      rel="noopener"
                      sx={{
                        wordBreak: "break-all",
                        display: "block",
                        fontSize: "0.875rem",
                      }}
                    >
                      {qrCode.orderUrl}
                    </Link>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default QRCodeManagement;
